/**
 * The language of the screens before there is an account: the sign-in page,
 * signing up, and the moment the account is made.
 *
 * A browser set to Spanish gets Spanish from the first screen on; the sign-in
 * page's "Español" / "English" switch chooses by hand, and that choice is kept
 * on this device. Sign-up sends it with the new account (CompleteSignup), so
 * the account's preferences, the welcome email and every screen after start
 * in it. Once signed in, the account's own preferences decide
 * (UserConfigProvider), and the language they say is kept on this device too
 * (keepStartLanguage), so the next visit starts in it instead of drawing in the
 * browser's language first (B72).
 *
 * Storage is read and written inside try: a browser with site data blocked
 * throws on any touch of it, and the browser's language still works then.
 *
 * Who uses it: main.tsx (applyStartLanguage, before the first draw),
 * LoginPage (the switch), UserConfigProvider (before there is an account)
 * and features/gift (the gift page's language, for a buyer with no account).
 *
 * @module startLanguage
 */

import { setLanguage } from '@/shared/i18n/i18nSetup';
import type { Language } from '@/shared/types/i18n';
import { START_LANGUAGE_STORAGE_KEY } from '@/config/tunables/shared';

/**
 * Whether a language tag is one Grow speaks, read as its first part: "es-MX" is Spanish.
 *
 * @param tag - A browser language tag, or a kept choice.
 * @returns The language, or null for one Grow has no words for.
 */
function readLanguage(tag: string | null | undefined): Language | null {
  const base = String(tag ?? '').trim().toLowerCase().split(/[-_]/)[0];
  return base === 'es' || base === 'en' ? base : null;
}

/**
 * The browser's language, the first of its preferred ones Grow speaks;
 * English when none is.
 *
 * @returns 'en' or 'es'.
 */
export function browserLanguage(): Language {
  if (typeof navigator === 'undefined') return 'en';
  const tags = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of tags) {
    const language = readLanguage(tag);
    if (language) return language;
  }
  return 'en';
}

/**
 * The language chosen on the sign-in page on this device, if one was.
 *
 * @returns The language, or null when none was chosen (or storage is refused).
 */
export function keptLanguage(): Language | null {
  try {
    return readLanguage(window.localStorage.getItem(START_LANGUAGE_STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * The language for the screens before an account: the one chosen here, else the browser's.
 *
 * @returns 'en' or 'es'.
 */
export function startLanguage(): Language {
  return keptLanguage() ?? browserLanguage();
}

/**
 * Chooses the language by hand (the sign-in page's switch): kept on this
 * device and put on the page at once.
 *
 * @param language - English or Spanish.
 */
export function chooseStartLanguage(language: Language): void {
  try { window.localStorage.setItem(START_LANGUAGE_STORAGE_KEY, language); } catch { /* not kept; still used now */ }
  setLanguage(language);
}

/**
 * Keeps the language the signed-in account reads in as this device's start language, without changing the page
 * (applyPreferences does that): the next cold load, of any page, starts in it. Kept the same way the sign-in
 * page's switch keeps its choice.
 *
 * @param language - The account's language.
 */
export function keepStartLanguage(language: Language): void {
  try { window.localStorage.setItem(START_LANGUAGE_STORAGE_KEY, language); } catch { /* not kept; the account's preference still decides */ }
}

/** Puts the start language on the page, before anything is drawn (main). */
export function applyStartLanguage(): void {
  setLanguage(startLanguage());
}
